/**
 * The touch tier: a virtual stick, a throttle lever, and six buttons big enough to hit.
 *
 * ```
 *   ┌──────────────────────────────────────────────────────┐
 *   │                                                  ▓ G │   gear
 *   │                                                  ▓ F │   flaps
 *   │                                                  ▓ B │   brakes (hold)
 *   │      ╭───╮                                   ║   ▓ ✦ │   fire (hold)
 *   │     ( ● )                                    ║   ▓ ◉ │   camera
 *   │      ╰───╯                                   ║   ▓ ⏸ │   pause
 *   │      ◀   ▶                                 throttle  │
 *   └──────────────────────────────────────────────────────┘
 * ```
 *
 * ## Why this is DOM and not drawn into the canvas
 *
 * Because a control the pilot's thumb is resting on must respond in the time it takes the browser
 * to deliver a pointer event, not in the time it takes the simulator to render a frame. On a phone
 * at the Low tier those are 2 ms and 16 ms, and the difference between them is the whole feel of a
 * touch control. It also means the stick keeps working during a frame the renderer stalls on,
 * which is exactly when the pilot most wants it to.
 *
 * ## Multi-touch, and why each control captures its own pointer
 *
 * Flying needs the stick and the throttle at the same time, and the brake and the stick at the
 * same time. So every control tracks its own `pointerId` and captures it: a finger that starts on
 * the stick stays with the stick even when it wanders over the throttle, and a second finger on the
 * throttle is a separate stream. Without capture, dragging off a control silently drops the input
 * and the aeroplane goes to neutral mid-manoeuvre.
 *
 * ## The rudder is two buttons, and that is on purpose
 *
 * There is no room for a second stick, and there does not need to be one: the auto-coordination
 * assist is on by default and is doing the rudder in flight (see `input/coordination.ts`). What is
 * left is ground steering and a deliberate slip, and both are fine as a pair of held buttons.
 *
 * `updateFixed(dt)`: no — this is an *input source*. It accumulates browser events into fields, and
 * `FlightControls.updateFixed` folds those fields into the command set at 120 Hz along with the
 * keyboard and the gamepad. Nothing here runs on a clock.
 */

import { shapeAxis } from './axes.js';
import type { TouchSource } from './flightControls.js';

/** Stick travel from the base centre to full deflection, as a fraction of the base radius. */
const STICK_TRAVEL = 0.9;

/**
 * Shaping for the stick.
 *
 * A thumb on glass is far less precise than a hand on a stick and has no force feedback at all, so
 * the deadzone is generous and the expo is heavy: the middle third of the travel is fine control
 * and the outer third still reaches the stops. Without the expo, level flight on a phone is a
 * continuous series of over-corrections — measured by trying it, which is the only way.
 */
const STICK_SHAPING = { deadzone: 0.06, expo: 0.6 } as const;

/** Where a control's finger is, in the geometry that matters to it. */
interface Grab {
  pointerId: number;
}

const NO_GRAB: Grab = { pointerId: -1 };

/**
 * Capture and release, neither of which is allowed to throw.
 *
 * Both `setPointerCapture` and `releasePointerCapture` reject a pointer id that is no longer
 * active — a finger lifted while the page was hidden, a pointer the browser cancelled during a
 * system gesture, a stale id after an orientation change. Uncaught, that throw comes out of a
 * pointer listener, reaches the window error handler and puts the full-screen error overlay in
 * front of somebody who was mid-approach and merely lifted a thumb at an awkward moment.
 *
 * Losing capture is a real but small degradation: the control keeps working, and a drag that
 * wanders off it stops tracking instead of following the finger. That is worth strictly more than
 * a crash screen.
 */
function capture(element: HTMLElement, pointerId: number, take: boolean): void {
  try {
    if (take) element.setPointerCapture(pointerId);
    else element.releasePointerCapture(pointerId);
  } catch {
    // See above: no capture is a worse control, and a throw here is a dead simulator.
  }
}

export interface TouchControlsOptions {
  /** Raise and lower the gear. */
  onGear: () => void;
  /** One flap detent down; wraps to clean at the last one — see the button's own note. */
  onFlaps: () => void;
  onCamera: () => void;
  onPause: () => void;
  parent?: HTMLElement;
}

/**
 * The on-screen controls, and the {@link TouchSource} the command layer reads them through.
 *
 * Implements `TouchSource` directly rather than owning one: there is exactly one of these per
 * session and the indirection would buy nothing.
 */
export class TouchControls implements TouchSource {
  readonly element: HTMLDivElement;

  private readonly options: TouchControlsOptions;
  private readonly stickBase: HTMLDivElement;
  private readonly stickKnob: HTMLDivElement;
  private readonly throttleTrack: HTMLDivElement;
  private readonly throttleFill: HTMLDivElement;
  private readonly throttleLabel: HTMLSpanElement;

  private stickGrab: Grab = NO_GRAB;
  private throttleGrab: Grab = NO_GRAB;

  /** Raw stick deflection, −1…1, before shaping. */
  private rawX = 0;
  private rawY = 0;
  private throttleValue = 0;
  private yawLeft = false;
  private yawRight = false;
  private brakesHeld = false;
  private fireHeld = false;
  private visibleValue = false;

  constructor(options: TouchControlsOptions) {
    this.options = options;
    const parent = options.parent ?? document.body;

    this.element = document.createElement('div');
    this.element.className = 'touch';
    this.element.hidden = true;

    // --- the stick -----------------------------------------------------------------------
    this.stickBase = document.createElement('div');
    this.stickBase.className = 'touch__stick';
    this.stickKnob = document.createElement('div');
    this.stickKnob.className = 'touch__knob';
    this.stickBase.append(this.stickKnob);
    this.bindStick();

    // --- the rudder ----------------------------------------------------------------------
    const rudder = document.createElement('div');
    rudder.className = 'touch__rudder';
    rudder.append(
      this.holdButton('◀', 'Left rudder', (down) => {
        this.yawLeft = down;
      }),
      this.holdButton('▶', 'Right rudder', (down) => {
        this.yawRight = down;
      }),
    );

    const left = document.createElement('div');
    left.className = 'touch__left';
    left.append(this.stickBase, rudder);

    // --- the throttle --------------------------------------------------------------------
    this.throttleTrack = document.createElement('div');
    this.throttleTrack.className = 'touch__throttle';
    this.throttleFill = document.createElement('div');
    this.throttleFill.className = 'touch__throttleFill';
    this.throttleLabel = document.createElement('span');
    this.throttleLabel.className = 'touch__throttleLabel';
    this.throttleLabel.textContent = '0%';
    this.throttleTrack.append(this.throttleFill, this.throttleLabel);
    this.bindThrottle();

    // --- the buttons ---------------------------------------------------------------------
    const buttons = document.createElement('div');
    buttons.className = 'touch__buttons';
    buttons.append(
      this.tapButton('GEAR', 'Gear up or down', () => this.options.onGear()),
      /*
       * One flaps button rather than two.
       *
       * `F` and `V` on the keyboard step down and up, but two buttons here would each be half the
       * size and a mis-hit costs a flap setting on short final. `FlightControls.cycleFlaps` clamps
       * at both ends, so this one steps down and the pilot uses the *keyboard* — or lands — to
       * come back up. Wrapping instead would mean a fat-fingered extra press retracts the flaps at
       * fifty feet, which is a good deal worse than not being able to retract them at all.
       */
      this.tapButton('FLAP', 'One flap detent down', () => this.options.onFlaps()),
      this.holdButton('BRAKE', 'Wheel brakes', (down) => {
        this.brakesHeld = down;
      }, 'touch__button'),
      this.holdButton('FIRE', 'Fire', (down) => {
        this.fireHeld = down;
      }, 'touch__button touch__button--fire'),
      this.tapButton('VIEW', 'Cycle camera', () => this.options.onCamera()),
      this.tapButton('❙❙', 'Pause', () => this.options.onPause()),
    );

    const right = document.createElement('div');
    right.className = 'touch__right';
    right.append(buttons, this.throttleTrack);

    this.element.append(left, right);
    parent.append(this.element);
  }

  // --- TouchSource ------------------------------------------------------------------------

  /**
   * Is the pilot touching anything?
   *
   * False when every control is at rest, and that is what stops the touch layer pinning the
   * aeroplane to neutral on a tablet that also has a keyboard: an untouched stick contributes
   * nothing rather than contributing zero.
   */
  get active(): boolean {
    return (
      this.visibleValue &&
      (this.stickGrab !== NO_GRAB || this.rawX !== 0 || this.rawY !== 0 || this.yawLeft || this.yawRight)
    );
  }

  get pitch(): number {
    // Screen y grows downward, and pulling *back* — down the screen — is nose up.
    return shapeAxis(-this.rawY, STICK_SHAPING);
  }

  get roll(): number {
    return shapeAxis(this.rawX, STICK_SHAPING);
  }

  get yaw(): number {
    return (this.yawRight ? 1 : 0) - (this.yawLeft ? 1 : 0);
  }

  /** Null while the touch layer is hidden, so it never overrides a keyboard throttle. */
  get throttle(): number | null {
    return this.visibleValue ? this.throttleValue : null;
  }

  get brakes(): boolean {
    return this.visibleValue && this.brakesHeld;
  }

  get fire(): boolean {
    return this.visibleValue && this.fireHeld;
  }

  // --- visibility -------------------------------------------------------------------------

  get visible(): boolean {
    return this.visibleValue;
  }

  /** Show or hide the whole layer. Hiding releases every held control. */
  setVisible(visible: boolean): void {
    if (visible === this.visibleValue) return;
    this.visibleValue = visible;
    this.element.hidden = !visible;
    if (!visible) this.releaseAll();
  }

  /**
   * Seed the throttle lever from wherever the simulation's throttle already is.
   *
   * Without it the lever reads 0 % on a session that spawned with the engine running at idle, and
   * the pilot's first touch snaps the power to wherever their thumb landed.
   */
  setThrottle(value: number): void {
    this.throttleValue = Math.min(1, Math.max(0, value));
    this.paintThrottle();
  }

  dispose(): void {
    this.element.remove();
  }

  // --- the stick --------------------------------------------------------------------------

  private bindStick(): void {
    this.stickBase.addEventListener('pointerdown', (event: PointerEvent) => {
      if (this.stickGrab !== NO_GRAB) return;
      this.stickGrab = { pointerId: event.pointerId };
      capture(this.stickBase, event.pointerId, true);
      // Not `preventDefault` — `touch-action: none` in the stylesheet is what stops the page
      // scrolling, and it does it before the event is dispatched rather than after.
      this.moveStick(event);
    });
    this.stickBase.addEventListener('pointermove', (event: PointerEvent) => {
      if (event.pointerId !== this.stickGrab.pointerId) return;
      this.moveStick(event);
    });
    const release = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickGrab.pointerId) return;
      capture(this.stickBase, event.pointerId, false);
      this.stickGrab = NO_GRAB;
      // Springs to centre, like the stick it is standing in for.
      this.rawX = 0;
      this.rawY = 0;
      this.paintStick();
    };
    this.stickBase.addEventListener('pointerup', release);
    this.stickBase.addEventListener('pointercancel', release);
  }

  private moveStick(event: PointerEvent): void {
    const box = this.stickBase.getBoundingClientRect();
    const radius = Math.min(box.width, box.height) * 0.5 * STICK_TRAVEL;
    if (radius <= 0) return;
    const deflection = stickDeflection(
      event.clientX - (box.left + box.width * 0.5),
      event.clientY - (box.top + box.height * 0.5),
      radius,
    );
    this.rawX = deflection.x;
    this.rawY = deflection.y;
    this.paintStick();
  }

  private paintStick(): void {
    const box = this.stickBase.getBoundingClientRect();
    const radius = Math.min(box.width, box.height) * 0.5 * STICK_TRAVEL;
    this.stickKnob.style.transform =
      `translate(-50%, -50%) translate(${(this.rawX * radius).toFixed(1)}px, ${(this.rawY * radius).toFixed(1)}px)`;
  }

  // --- the throttle -----------------------------------------------------------------------

  private bindThrottle(): void {
    const set = (event: PointerEvent): void => {
      const box = this.throttleTrack.getBoundingClientRect();
      this.throttleValue = throttleFromTrack(event.clientY, box.top, box.height);
      this.paintThrottle();
    };
    this.throttleTrack.addEventListener('pointerdown', (event: PointerEvent) => {
      if (this.throttleGrab !== NO_GRAB) return;
      this.throttleGrab = { pointerId: event.pointerId };
      capture(this.throttleTrack, event.pointerId, true);
      set(event);
    });
    this.throttleTrack.addEventListener('pointermove', (event: PointerEvent) => {
      if (event.pointerId !== this.throttleGrab.pointerId) return;
      set(event);
    });
    const release = (event: PointerEvent): void => {
      if (event.pointerId !== this.throttleGrab.pointerId) return;
      capture(this.throttleTrack, event.pointerId, false);
      this.throttleGrab = NO_GRAB;
      // Deliberately *not* reset: a throttle is a lever, and it stays where it is put.
    };
    this.throttleTrack.addEventListener('pointerup', release);
    this.throttleTrack.addEventListener('pointercancel', release);
  }

  private paintThrottle(): void {
    const percent = Math.round(this.throttleValue * 100);
    this.throttleFill.style.height = `${percent}%`;
    const label = `${percent}%`;
    if (this.throttleLabel.textContent !== label) this.throttleLabel.textContent = label;
  }

  // --- buttons ----------------------------------------------------------------------------

  private tapButton(label: string, description: string, run: () => void): HTMLButtonElement {
    const button = this.makeButton(label, description, 'touch__button');
    // `pointerdown` rather than `click`: a click waits for the pointer to come back up, and on a
    // control surface that costs the whole press duration. Nothing here is destructive enough to
    // want the "slide off to cancel" behaviour a click gives.
    button.addEventListener('pointerdown', (event: PointerEvent) => {
      event.preventDefault();
      run();
    });
    return button;
  }

  private holdButton(
    label: string,
    description: string,
    set: (down: boolean) => void,
    className = 'touch__rudderButton',
  ): HTMLButtonElement {
    const button = this.makeButton(label, description, className);
    let held = -1;
    button.addEventListener('pointerdown', (event: PointerEvent) => {
      event.preventDefault();
      held = event.pointerId;
      capture(button, event.pointerId, true);
      button.classList.add('touch__button--down');
      set(true);
    });
    const release = (event: PointerEvent): void => {
      if (event.pointerId !== held) return;
      held = -1;
      capture(button, event.pointerId, false);
      button.classList.remove('touch__button--down');
      set(false);
    };
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    return button;
  }

  private makeButton(label: string, description: string, className: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.setAttribute('aria-label', description);
    return button;
  }

  /**
   * Let go of everything.
   *
   * Called when the layer is hidden — by a pause, a menu, or the landscape prompt. Without it a
   * finger that was on the brake when the menu opened holds the brake for the rest of the session,
   * because the `pointerup` lands on an element that is no longer there to hear it.
   */
  private releaseAll(): void {
    this.stickGrab = NO_GRAB;
    this.throttleGrab = NO_GRAB;
    this.rawX = 0;
    this.rawY = 0;
    this.yawLeft = false;
    this.yawRight = false;
    this.brakesHeld = false;
    this.fireHeld = false;
    for (const button of this.element.querySelectorAll('.touch__button--down')) {
      button.classList.remove('touch__button--down');
    }
    this.paintStick();
  }
}

/**
 * A finger's offset from the base centre, as a deflection in −1…1 on each axis.
 *
 * Clamped **radially**, not per axis: clamping each axis independently lets the corners of the
 * square reach √2 of full deflection, so a diagonal input commands 141 % of the roll a pure
 * sideways one does. On a stick that is a real handling difference and it feels like the control
 * getting stronger when you pull diagonally.
 */
export function stickDeflection(
  offsetXPx: number,
  offsetYPx: number,
  radiusPx: number,
): { x: number; y: number } {
  if (!(radiusPx > 0)) return { x: 0, y: 0 };
  const distance = Math.hypot(offsetXPx, offsetYPx);
  if (distance <= radiusPx) return { x: offsetXPx / radiusPx, y: offsetYPx / radiusPx };
  const scale = radiusPx / distance;
  return { x: (offsetXPx * scale) / radiusPx, y: (offsetYPx * scale) / radiusPx };
}

/**
 * Where a finger on the throttle track puts the lever, 0…1.
 *
 * Inverted, because up is more power on every throttle quadrant ever built and screen y grows the
 * other way. Clamped, because a capturing pointer reports positions well outside the element.
 */
export function throttleFromTrack(clientYPx: number, trackTopPx: number, trackHeightPx: number): number {
  if (!(trackHeightPx > 0)) return 0;
  const fromTop = (clientYPx - trackTopPx) / trackHeightPx;
  return Math.min(1, Math.max(0, 1 - fromTop));
}
